/*
 * Collection card - discount badge and a price block that reads like the PDP.
 *
 *     +--------------------+
 *     | [-30%]         (o) |   badge left, wishlist control right
 *     |      image         |
 *     +--------------------+
 *     Product title
 *     PRICE  $1,307.00 USD
 *     $1,867.00 USD            struck
 *
 * MEASURED FROM THE REFERENCE
 *     .er-saleribbon  11px / 700, white on a solid red, radius 2px,
 *                     absolute, top 12px right 12px
 *     .er-price       flex, align-items baseline, wrap, gap 4px 8px
 *     .amt            24px / 600
 *     .er-was         16px / 600, grey, struck
 *
 * THE RATING AND THE URGENCY LINE
 *
 * 1. The star rating was left out in round 45 because there was no rating data
 *    on a card at all, and a row reading "0.0 (0)" on every tile is worse than
 *    no row. The 80-review set has since landed, so the average and the count
 *    are computed from it - and rendered ONLY for a product that actually has
 *    reviews. Measured on the live grid: 14 of 20 engagement ring cards carry
 *    one; 0 of 20 on /collections/all, which therefore shows none rather than
 *    an invented figure.
 *
 * 2. "Ends soon" was left out for being 140 urgency claims on a 140-card grid.
 *    It has since been asked for, and is tied to the same discount test that
 *    decides the badge - so a product that is not on sale claims no deadline.
 *
 * THE TWO CORNERS ARE SPLIT ON PURPOSE
 * The badge sits top LEFT and the wishlist heart keeps the top right. Sharing
 * the right-hand corner meant dodging .card-product__group.group-right - a
 * 36px control at top:10 right:10 - by holding the badge 54px off the edge,
 * which read as a badge that had drifted inward. Split across the corners each
 * one is flush to its own edge and they cannot collide at any card width.
 *
 * COLOURS
 * The badge is #b3261e, the red already used for urgency on the product page.
 * On a dense grid a discount badge has to be scannable, which is what red is
 * for; the quieter gold chip stays on the PDP where it sits beside a price.
 * Type is left entirely to the theme - only sizes and weights are set.
 *
 * THE PERCENTAGE IS REAL
 * Parsed from the two figures the card already renders, never assumed. If
 * either fails to parse, or the compare price is not above the sale price,
 * the card gets no badge at all.
 *
 * BESTSELLER (round 110)
 * A product with MORE THAN 15 reviews gets a "Bestseller" badge. The count is
 * the larger of the app's figure (Air Reviews / Shopify rating metafields,
 * printed on the card as data-civia-review-count) and the review set behind
 * the rating row - the larger, not the sum, so a review in both is never
 * counted twice. Change BESTSELLER_MIN_REVIEWS to move the threshold.
 * Badges stack in one top-left column: the discount, then Bestseller.
 */

(function () {
  'use strict';

  var STYLE_ID = 'civia-card-style';
  var MINUS = String.fromCharCode(8722); // true minus, as the reference uses
  var BESTSELLER_MIN_REVIEWS = 15; // "more than 15 reviews"

  var CSS =
    '.civia-cb{position:absolute;top:12px;left:12px;z-index:2;' +
    'display:inline-block;padding:5px 9px;border-radius:2px;' +
    'background:#b3261e;color:#fff;font-size:11px;font-weight:700;' +
    'letter-spacing:.02em;line-height:1.2;white-space:nowrap;pointer-events:none}' +
    /* Round 110: one absolutely positioned column holds the badges, so each
       badge flows under the one before it and none has to know whether the
       others are there. Inside it the discount badge is no longer absolute. */
    '.civia-badges{position:absolute;top:12px;left:12px;z-index:2;display:flex;' +
    'flex-direction:column;align-items:flex-start;gap:6px;pointer-events:none}' +
    '.civia-badges .civia-cb{position:static;order:1}' +
    /* Bestseller: the dark and gold of the announcement bar, so it reads as
       an accolade rather than another price signal. */
    '.civia-bs{order:2;display:inline-flex;align-items:center;gap:4px;padding:5px 9px;' +
    'border-radius:2px;background:#1c1917;color:#f4efe7;font-size:11px;font-weight:700;' +
    'letter-spacing:.06em;line-height:1.2;text-transform:uppercase;white-space:nowrap}' +
    '.civia-bs__star{width:10px;height:10px;fill:#d8b76d;flex:none}' +
    /* Price row: a big current figure and a small struck one, sharing a
       baseline - the same relationship the product page now uses. */
    '.card-price .price__sale{display:flex;align-items:baseline !important;' +
    'flex-wrap:wrap;gap:4px 8px}' +
    '.card-price .price-item--sale{font-size:24px !important;font-weight:600;line-height:1.15}' +
    '.card-price .price-item--regular{font-size:15px !important;font-weight:400;color:#8a8a8a}' +
    '.civia-cl{font-size:10px;letter-spacing:.14em;text-transform:uppercase;' +
    'color:#8a8a8a;align-self:center}' +
    /* Rating row, under the title. The same gold the reviews page uses for
       its stars, so one product reads the same in a grid and on its page. */
    '.civia-cr{display:flex;align-items:center;gap:6px;margin:4px 0 2px}' +
    '.civia-cr__stars{display:inline-flex;gap:1px}' +
    '.civia-cr__star{width:13px;height:13px;fill:#d8d2cb;flex:none}' +
    '.civia-cr__star.is-on{fill:#c9a227}' +
    '.civia-cr__avg{font-size:13px;font-weight:600;color:#232323}' +
    '.civia-cr__count{font-size:12px;color:#8a8a8a}' +
    /* The urgency line under the price. Red is doing semantic work here,
       not decoration, so it is the same #b3261e as the discount badge. */
    /* Round 80: it now sits INSIDE the price row, so it carries no top
       margin and must not wrap away from the figures beside it. */
    '.civia-ce{display:inline-flex;align-items:center;gap:5px;' +
    'font-size:10px;font-weight:600;letter-spacing:.08em;white-space:nowrap;' +
    'text-transform:uppercase;color:#b3261e}' +
    '.civia-ce__dot{width:5px;height:5px;border-radius:50%;background:#b3261e;flex:none}' +
    /* Round 80 - the reference card's shape.

       The option rows sit ABOVE the price there, and the DOM order is
       title / summary / price / options. Reordering with flex `order`
       rather than moving nodes keeps the markup Shopify renders intact,
       so a theme-editor change or an app that re-renders a card cannot
       undo it. Every child is given a width so the flex column does not
       shrink-wrap them. */
    '.card-information__wrapper{display:flex;flex-direction:column;text-align:left}' +
    '.card-information__wrapper > *{width:100%}' +
    /* Round 81: `a.card-title` inside carries margin-bottom:17px that no
       stylesheet here can override - a CDN rule outranks it, and even an
       inline !important did not move it. Two clamped lines need 48px, so
       the box is held there and the dead margin is clipped. The theme
       already limits the title to two lines, so no text is lost. */
    '.card-information__wrapper .card__heading{order:1;height:48px;' +
    'overflow:hidden;margin:0 0 4px}' +
    '.card-information__wrapper .civia-cr{order:2}' +
    '.card-information__wrapper .card-summary{order:3}' +
    '.card-information__wrapper .variant-section{order:4;margin:6px 0 10px;gap:8px}' +
    '.card-information__wrapper .card-price{order:5;margin:0}' +
    /* One line: label, price, struck price, deadline. */
    '.card-price .price{width:100%}' +
    '.card-price dl{margin:0}' +
    /* The framed card. box-sizing is set explicitly because the grid sizes
       these in percentage columns - without it the padding below would push
       four-up rows into three. */
    '.product-item{box-sizing:border-box;background:#fff;' +
    'border:1px solid #e7e3dc;padding:14px}' +
    '.product-item *,.product-item *::before,.product-item *::after{box-sizing:border-box}' +
    /* Round 81 - the card has to fit the screen.

       `.card-media--square` is a padding-bottom:100% square, so the media
       height IS the column width and the card grows without bound as the
       screen gets wider. At ~1912px the column is ~440px and the card
       overflowed the viewport. `min()` bounds it: tall screens keep the
       exact square they have today, short wide ones stop at 42vh.

       It MUST stay a padding-bottom rule. Rewriting it as aspect-ratio +
       max-height blanked every image on the grid - the <img> is absolutely
       positioned and sizes against this padding box. */
    '@media (min-width:1025px){' +
    '.card-media--square{padding-bottom:min(100%, 42vh) !important}}' +
    '@media (max-width:640px){' +
    '.card-price .price-item--sale{font-size:20px !important}' +
    '.product-item{padding:10px}' +
    '.card-price .price-item--regular{font-size:13px !important}' +
    '.civia-cb{top:8px;left:8px;padding:4px 7px;font-size:10px}' +
    '.civia-badges{top:8px;left:8px;gap:4px}' +
    '.civia-bs{padding:4px 7px;font-size:10px}' +
    '.civia-cr__star{width:12px;height:12px}.civia-cr__avg{font-size:12px}' +
    '.civia-ce{font-size:10px}}';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* "from $1,307.00 USD" -> 1307. Returns NaN for anything it cannot read,
     which is what suppresses the badge rather than guessing a number. */
  function amount(text) {
    if (!text) return NaN;

    var digits = String(text).replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (!digits) return NaN;

    var value = parseFloat(digits);
    return value > 0 ? value : NaN;
  }

  function visibleStruck(scope) {
    var all = scope.querySelectorAll('.price-item--regular');

    for (var i = 0; i < all.length; i++) {
      /* There are two elements with this class - one hidden in .price__last.
         Only the rendered one carries the compare-at figure. */
      if (all[i].getBoundingClientRect().height > 0) return all[i];
    }

    return null;
  }

  /* Built once from the review set rather than scanned per card: 20 cards
     against 80 reviews is 1,600 comparisons for something that indexes in one
     pass. Returns null - never a zero - for a product with no reviews, so the
     caller renders no row at all. */
  var ratingIndex = null;

  function buildRatingIndex() {
    ratingIndex = {};

    var list = window.CIVIA_SAMPLE_REVIEWS;
    if (!Array.isArray(list)) return;

    for (var i = 0; i < list.length; i++) {
      var review = list[i];
      var link = review && review.product && review.product.productLink;
      if (!link) continue;

      var rate = Math.max(1, Math.min(5, Math.round(Number(review.rate) || 0)));
      var bucket = ratingIndex[link] || (ratingIndex[link] = { sum: 0, count: 0 });

      bucket.sum += rate;
      bucket.count++;
    }
  }

  function ratingFor(card) {
    /* Rebuilt while empty: the review data is a separate deferred script and
       may not have run when the first card is decorated. */
    if (!ratingIndex || !Object.keys(ratingIndex).length) buildRatingIndex();

    var link = card.querySelector('.card-title');
    if (!link) return null;

    var href = link.getAttribute('href') || '';
    var match = href.match(/\/products\/[^\/?#]+/);
    if (!match) return null;

    var bucket = ratingIndex[match[0]];
    if (!bucket || !bucket.count) return null;

    return {
      average: Math.round((bucket.sum / bucket.count) * 10) / 10,
      count: bucket.count
    };
  }

  function ratingStars(average) {
    var filled = Math.round(average);
    var out = '';

    for (var i = 1; i <= 5; i++) {
      out +=
        '<svg class="civia-cr__star' + (i <= filled ? ' is-on' : '') +
        '" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"/></svg>';
    }

    return out;
  }

  /* Under the title, above the price - the order the reference uses. */
  function addRating(card) {
    var heading = card.querySelector('.card__heading');
    if (!heading || !heading.parentNode) return;
    if (card.querySelector('.civia-cr')) return;

    var rating = ratingFor(card);
    if (!rating) return; // no reviews: no row, rather than "0.0 (0)"

    var row = document.createElement('div');
    row.className = 'civia-cr';
    row.innerHTML =
      '<span class="civia-cr__stars" role="img" aria-label="' +
      rating.average + ' out of 5 stars">' + ratingStars(rating.average) + '</span>' +
      '<span class="civia-cr__avg">' + rating.average.toFixed(1) + '</span>' +
      '<span class="civia-cr__count">(' + rating.count + ')</span>';

    heading.parentNode.insertBefore(row, heading.nextSibling);
  }

  /*
   * `$2,000.00 USD` -> `$2,000`, as the reference prints it.
   *
   * Done here rather than by changing the store's currency format, which would
   * restyle every price in the shop including checkout, and rather than in
   * Liquid, which would mean editing a 29 KB theme snippet for a cosmetic rule.
   *
   * Only TEXT nodes are touched, so the `<span class="text">from</span>` prefix
   * that Shopify puts inside the same element survives untouched. Idempotent -
   * a figure already tidied has no cents or suffix left to match, so repeat
   * passes (this runs on every mutation) leave it alone.
   */
  function tidyMoney(root) {
    var items = root.querySelectorAll('.price-item');

    for (var i = 0; i < items.length; i++) {
      var nodes = items[i].childNodes;

      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (node.nodeType !== 3 || node.nodeValue.indexOf('$') === -1) continue;

        var tidied = node.nodeValue.replace(
          /\$\s?([\d,]+)(?:\.\d{2})?(?:\s*[A-Za-z]{3})?/g,
          function (match, figure) { return '$' + figure; }
        );

        if (tidied !== node.nodeValue) node.nodeValue = tidied;
      }
    }
  }

  /* Only ever called where a real discount was parsed, so the deadline is
     attached to an actual offer rather than printed on every tile.

     Round 80 moved it INTO the price row, inline after the struck figure,
     which is where the reference puts it. It used to close the card below
     the shape and metal rows; those rows now sit above the price, so the
     old anchor would have stranded the deadline in the middle of the
     card. */
  function addEndsSoon(card, priceBlock) {
    if (!card || card.querySelector('.civia-ce')) return;

    /* Round 80: into the price row itself, so the card reads
       `PRICE $569  $1,138  * ENDS SOON` on one line as the reference does.
       `.price__sale` is the flex row the label already sits in; falling
       back to the price block keeps the line from being lost if a card
       renders its price some other way. */
    var row = priceBlock.querySelector('.price__sale') || priceBlock;

    var line = document.createElement('div');
    line.className = 'civia-ce';
    line.innerHTML = '<span class="civia-ce__dot"></span>Ends soon';

    row.appendChild(line);
  }

  /* Round 110 - the top-left badge column, created on first use. */
  function badgeStack(media) {
    var stack = media.querySelector('.civia-badges');

    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'civia-badges';
      media.appendChild(stack);
    }

    return stack;
  }

  function reviewCount(card) {
    var holder = card.querySelector('[data-civia-review-count]');
    var app = holder ? parseInt(holder.getAttribute('data-civia-review-count'), 10) || 0 : 0;
    var rating = ratingFor(card);

    return Math.max(app, rating ? rating.count : 0);
  }

  function addBestseller(card, media) {
    if (media.querySelector('.civia-bs')) return;
    if (reviewCount(card) <= BESTSELLER_MIN_REVIEWS) return;

    var badge = document.createElement('span');
    badge.className = 'civia-bs';
    badge.innerHTML =
      '<svg class="civia-bs__star" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
      '<path d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"/></svg>' +
      'Bestseller';

    badgeStack(media).appendChild(badge);
  }

  function decorate(priceBlock) {
    var card = priceBlock.closest('.product') || priceBlock.closest('.card');
    if (!card) return;

    /* Both of these are independent of any discount - a full-price product
       still carries reviews, and still prints a price that needs tidying -
       so they run before the badge's own early returns. */
    addRating(card);
    tidyMoney(priceBlock);

    var media = card.querySelector('.card-product');
    if (!media) return;

    /* Independent of any discount too, so before the badge's early returns. */
    addBestseller(card, media);

    /* Idempotence comes from the badge already being there, NOT from a
       "processed" flag. An earlier version set that flag before checking
       whether the figures were readable, so the first pass - which runs
       before the struck price has been laid out - marked every card done and
       the retry never fired. Nothing was ever decorated. */
    if (media.querySelector('.civia-cb')) return;

    var saleEl = priceBlock.querySelector('.price-item--sale');
    var struckEl = visibleStruck(priceBlock);
    if (!saleEl || !struckEl) return;

    var sale = amount(saleEl.textContent);
    var compare = amount(struckEl.textContent);
    if (isNaN(sale) || isNaN(compare) || compare <= sale) return;

    /* Label in front of the price, as the reference has it. */
    var row = priceBlock.querySelector('.price__sale');
    if (row && !row.querySelector('.civia-cl')) {
      var label = document.createElement('span');
      label.className = 'civia-cl';
      label.textContent = 'Price';
      row.insertBefore(label, row.firstChild);
    }

    addEndsSoon(card, priceBlock);

    var badge = document.createElement('span');
    badge.className = 'civia-cb';
    badge.textContent = MINUS + Math.round(((compare - sale) / compare) * 100) + '%';
    badgeStack(media).appendChild(badge);
  }

  function run() {
    var blocks = document.querySelectorAll('.card-price');

    for (var i = 0; i < blocks.length; i++) {
      try {
        decorate(blocks[i]);
      } catch (e) {}
    }
  }

  function start() {
    injectStyle();
    run();

    /* A MutationObserver alone is not enough here, and this cost a round to
       learn. The cards are server-rendered, so at DOMContentLoaded they are
       already in the DOM but not yet laid out - every struck price measures
       zero height, visibleStruck() returns null, and every card bails. No
       node is then inserted, so no childList mutation ever fires and the
       observer never gets a second chance. Nothing was ever decorated.

       So: retry on a short bounded schedule until layout settles, and keep
       the observer for genuinely late arrivals (Show More pagination). */
    window.addEventListener('load', run, false);

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts++;
      run();
      if (attempts >= 12) window.clearInterval(timer);
    }, 400);

    var grid = document.querySelector('#main-collection-product-grid') ||
      document.querySelector('.collection-products') || document.body;

    if (!window.MutationObserver) return;

    var pending = false;
    new window.MutationObserver(function () {
      if (pending) return;
      pending = true;

      window.setTimeout(function () {
        pending = false;
        run();
      }, 120);
    }).observe(grid, { childList: true, subtree: true });
  }

  /* Round 93 - exposed so civia-option-swap.js can finish a card it has just
     swapped in, in the same task, before the browser paints it. The observer
     above does catch a swapped card, but 120ms later - and for those 120ms the
     shopper saw "$615.00 USD", no badge, no Price label and no ENDS SOON.
     run() is idempotent (see decorate), so the observer's later pass changes
     nothing. */
  window.civiaCard = { run: run };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
